/**
 * CalendarDay Component
 *
 * Individual day cell in the calendar grid.
 */

import React from 'react';
import { CalendarDay as CalendarDayData } from '../hooks';

interface CalendarDayProps {
  date: Date;
  data?: CalendarDayData;
  isSelected: boolean;
  isPast: boolean;
  isToday: boolean;
  onMouseDown: () => void;
  onMouseEnter: () => void;
  onClick?: () => void;
}

export const CalendarDay: React.FC<CalendarDayProps> = ({
  date,
  data,
  isSelected,
  isPast,
  isToday,
  onMouseDown,
  onMouseEnter,
  onClick,
}) => {
  const getBackgroundClass = () => {
    if (isSelected) return 'bg-primary-100 border-primary-500 border-2';
    if (isPast) return 'bg-gray-50';
    if (data?.bookingId) {
      return data.bookingStatus === 'pending'
        ? 'bg-yellow-50 border-yellow-200'
        : 'bg-blue-50 border-blue-200';
    }
    if (data?.isBlocked) return 'bg-gray-100 border-gray-200';
    if (data?.customPrice) return 'bg-green-50 border-green-200';
    return 'bg-white hover:bg-gray-50 border-gray-100';
  };

  return (
    <div
      className={`min-h-[80px] md:min-h-[100px] border p-1 md:p-2 cursor-pointer select-none transition-colors ${getBackgroundClass()} ${isPast ? 'cursor-not-allowed opacity-60' : ''}`}
      onMouseDown={isPast ? undefined : onMouseDown}
      onMouseEnter={isPast ? undefined : onMouseEnter}
      onClick={onClick}
    >
      {/* Date Number */}
      <div className="flex items-center justify-between mb-1">
        <span
          className={`text-xs md:text-sm font-medium ${
            isPast ? 'text-gray-400' : 'text-gray-700'
          } ${
            isToday
              ? 'bg-primary-500 text-white w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center'
              : ''
          }`}
        >
          {date.getDate()}
        </span>
        <div className="flex items-center gap-0.5">
          {data?.isCheckIn && (
            <span className="w-2 h-2 rounded-full bg-green-500" title="Check-in" />
          )}
          {data?.isCheckOut && (
            <span className="w-2 h-2 rounded-full bg-red-500" title="Check-out" />
          )}
        </div>
      </div>

      {/* Price */}
      {!isPast && (
        <div className="mb-1">
          {data?.customPrice ? (
            <span className="text-xs font-semibold text-green-600">
              ${data.customPrice}
            </span>
          ) : data?.basePrice ? (
            <span className="text-xs text-gray-500">${data.basePrice}</span>
          ) : null}
        </div>
      )}

      {/* Booking Info */}
      {data?.guestName && (
        <div className="mt-auto">
          <span
            className={`text-xs truncate block ${
              data.bookingStatus === 'pending'
                ? 'text-yellow-700'
                : 'text-blue-700'
            }`}
          >
            {data.guestName}
          </span>
        </div>
      )}

      {/* Blocked Indicator */}
      {data?.isBlocked && !data.bookingId && (
        <div className="mt-auto">
          <span className="text-xs text-gray-500 hidden md:block">Blocked</span>
          <div className="md:hidden w-full h-1 bg-gray-300 rounded mt-1" />
        </div>
      )}

      {/* Weekend indicator */}
      {data?.isWeekend && !isPast && !data.bookingId && !data.isBlocked && (
        <div className="hidden md:block mt-auto">
          <span className="text-xs text-gray-400">Weekend</span>
        </div>
      )}
    </div>
  );
};

export default CalendarDay;
